import Price from '@components/common/data-display/price';
import {
  AspectRatio,
  Box,
  Button,
  Col,
  Divider,
  Drawer,
  Grid,
  Group,
  SimpleGrid,
  Stack,
  Text,
  Title,
  useMantineTheme,
} from '@mantine/core';
import { useDisclosure, useMediaQuery } from '@mantine/hooks';
import { MenuItemResponse } from '@models/response/category';
import cartSlice from '@reducers/cart';
import { AppDispatch } from '@stores/index';
import Image from 'next/image';
import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import { v4 as uuidV4 } from 'uuid';
import { safeString } from '../../../../utils/stringUtil';
import { MenuCategoryProps, MenuItemWithCategories } from '../types';
import MenuItemCard from './menuItemCard';
import MenuOrderItem from './menuItemCard/components/menuOrderItem';
import SelectToppingsModal from './menuItemCard/modals/selectToppingModal';

const MenuCategory: React.FC<MenuCategoryProps> = ({ menuCategory, search, isMobile }) => {
  const { title, menuItems } = menuCategory;
  const [opened, { open, close }] = useDisclosure(false);
  const [menuItemDetail, setMenuItemDetail] = useState<MenuItemWithCategories | undefined>(undefined);
  const largeScreen = useMediaQuery('(min-width: 50em)');
  const theme = useMantineTheme();
  const [showToppingModal, setShowToppingModal] = useState(false);

  const dispatch: AppDispatch = useDispatch();

  if (!menuItems?.length) {
    return null;
  }

  const filteredMenuItems = menuItems?.filter(
    (menuItem) => menuItem.active && safeString(menuItem?.title)?.indexOf(search) >= 0
  );

  if (filteredMenuItems.length === 0) return null;

  const onAddToCardClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (menuItemDetail?.toppingCategories?.length) {
      setShowToppingModal(true);
    } else {
      dispatch(
        cartSlice.actions.createOrder({
          ...menuItemDetail,
          count: 1,
          temporaryOrderId: uuidV4(),
          orderItemToppings: [],
        })
      );
    }
  };

  return (
    <>
      <Box id={safeString(title)}>
        <Stack>
          <Divider my="xs" label={title} labelPosition="center" />
          <SimpleGrid cols={2} spacing="lg" breakpoints={[{ maxWidth: 'xs', cols: 1, spacing: 'sm' }]}>
            {filteredMenuItems.map((menuItem, index) => (
              <Stack key={menuItem.id}>
                <MenuItemCard
                  menuItem={menuItem}
                  setMenuItemDetail={setMenuItemDetail}
                  setShowToppingModal={setShowToppingModal}
                  open={open}
                />
                {index !== filteredMenuItems.length - 1 && isMobile ? <Divider color="gray.1" /> : null}
              </Stack>
            ))}
          </SimpleGrid>
        </Stack>
      </Box>
      <Drawer
        opened={opened}
        onClose={() => {
          close();
          setMenuItemDetail(undefined);
        }}
        title="جزئیات محصول"
        size={largeScreen ? undefined : '100%'}
        position="bottom"
        transitionProps={{ transition: 'slide-up' }}
        zIndex={200}
      >
        <Grid>
          <Col xs={4} sm={2}>
            <AspectRatio ratio={1 / 1} mx="auto">
              <Image
                style={{ width: '100%', borderRadius: theme.radius.lg }}
                src={menuItemDetail?.imageUrl || ''}
                alt={menuItemDetail?.title || ''}
                height={343}
                width={343}
              />
            </AspectRatio>
          </Col>

          <Col xs={8} sm={10}>
            <Stack>
              <Title order={4} fz={14} weight="bold">
                {menuItemDetail?.title}
              </Title>

              <Text size="xs" color="brand.2">
                {menuItemDetail?.description}
              </Text>
              <Group position="apart">
                <Price price={menuItemDetail?.price || 0} discount={menuItemDetail?.discount} />

                <Button variant="outline" size="sm" disabled={!menuItemDetail?.available} onClick={onAddToCardClick}>
                  {menuItemDetail?.available ? 'افزودن' : 'تمام شد'}
                </Button>
              </Group>

              <MenuOrderItem orderItemId={safeString(menuItemDetail?.id)} />
            </Stack>
          </Col>
        </Grid>
      </Drawer>

      <SelectToppingsModal
        opened={showToppingModal}
        onClose={() => setShowToppingModal(false)}
        toppingCategories={menuItemDetail?.toppingCategories || []}
        menuItem={menuItemDetail as MenuItemResponse}
      />
    </>
  );
};

export default MenuCategory;
